/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import update from "immutability-helper"
import { createReducer } from "store/create-reducer"
import { getActionFail, getActionSuccess } from "store/duck/utils/actionUtils"
import { AxiosAction } from "types/api"
import {
  InventoryCategory,
  InventoryCategoryResponse,
} from "types/delv-platform-api/inventoryCategory"
import {
  BulkUploadTask,
  BulkUploadTaskStatus,
  GetBulkUploadTaskResponse,
} from "types/delv-platform-api/bulkUploadTask"
import { actions, actionTypes } from "./actions"
import { isOngoingTask } from "./utils"

export type ReducerType = {
  inventoryCategories: {
    isFetching: boolean
    data: InventoryCategory[]
    merchantID: string
    menuGroupID?: string
  }
  bulkTasks: {
    isFetching: boolean
    data: GetBulkUploadTaskResponse // for history table
  }
  ongoingBulkTasks: BulkUploadTask[] // for ongoing bulk task notification and loading status
}

export const initialState: ReducerType = {
  inventoryCategories: {
    isFetching: false,
    data: [],
    merchantID: "",
    menuGroupID: undefined,
  },
  bulkTasks: {
    isFetching: false,
    data: {
      tasks: [],
      nextCursor: "",
    },
  },
  ongoingBulkTasks: [],
}

export const reducer = createReducer<ReducerType, string, any>(initialState, {
  [actionTypes.FETCH_INVENTORY_CATEGORY]: (
    state: ReducerType,
    action: ReturnType<typeof actions.fetchInventoryCategory> & {
      payload?: {
        merchantID?: string
        menuGroupID?: string
      }
    },
  ) => {
    const merchantID =
      action.payload?.request?.headers?.merchantID ?? action.payload?.merchantID
    const menuGroupID =
      action.payload?.request?.params?.menuGroupID ??
      action.payload?.menuGroupID

    // first time fetch inventory category, or fetch for a different merchant
    if (merchantID && merchantID !== state.inventoryCategories.merchantID) {
      return update(state, {
        inventoryCategories: {
          $set: {
            isFetching: true,
            data: [],
            merchantID,
            menuGroupID,
          },
        },
      })
    }

    // happen in case done the fetch before, but keep doing the fetch later, eg for changing search keyword
    return update(state, {
      inventoryCategories: {
        $set: {
          ...state.inventoryCategories,
          isFetching: true,
        },
      },
    })
  },
  [getActionSuccess(actionTypes.FETCH_INVENTORY_CATEGORY)]: (
    state: ReducerType,
    action: AxiosAction<InventoryCategoryResponse>,
  ) => {
    const data = action?.payload?.data?.categories ?? []

    return update(state, {
      inventoryCategories: {
        $set: {
          ...state.inventoryCategories,
          isFetching: false,
          data,
        },
      },
    })
  },
  [getActionFail(actionTypes.FETCH_INVENTORY_CATEGORY)]: (
    state: ReducerType,
  ) => {
    return update(state, {
      inventoryCategories: {
        $set: {
          ...state.inventoryCategories,
          isFetching: false,
          data: [],
        },
      },
    })
  },
  [actionTypes.RESET_INVENTORY_CATEGORY]: (state: ReducerType) => {
    return update(state, {
      inventoryCategories: {
        $set: {
          ...state.inventoryCategories,
          data: [],
        },
      },
    })
  },
  [actionTypes.FETCH_BULK_TASKS]: (
    state: ReducerType,
    action: ReturnType<typeof actions.fetchBulkTasks>,
  ) => {
    const { startCursor } =
      (action as any).meta?.requestParams ?? action.payload.request.params

    if (!startCursor) {
      return update(state, {
        bulkTasks: {
          $set: {
            isFetching: true,
            data: {
              tasks: [],
              nextCursor: "",
            },
          },
        },
      })
    }

    return state
  },
  [getActionSuccess(actionTypes.FETCH_BULK_TASKS)]: (
    state: ReducerType,
    action: AxiosAction<GetBulkUploadTaskResponse>,
  ) => {
    const { nextCursor, tasks } = action?.payload?.data ?? {}

    return update(state, {
      bulkTasks: {
        $set: {
          isFetching: false,
          data: {
            tasks: tasks || [],
            nextCursor: nextCursor || "",
          },
        },
      },
      ongoingBulkTasks: {
        $set: (tasks || []).filter(task => isOngoingTask(task)),
      },
    })
  },
  [getActionFail(actionTypes.FETCH_BULK_TASKS)]: (state: ReducerType) => {
    return update(state, {
      bulkTasks: {
        $set: {
          isFetching: false,
          data: {
            tasks: [],
            nextCursor: "",
          },
        },
      },
    })
  },
  [getActionSuccess(actionTypes.POLLING_BULK_TASKS)]: (
    state: ReducerType,
    action: AxiosAction<GetBulkUploadTaskResponse>,
  ) => {
    const { tasks } = action?.payload?.data ?? {}
    if (!tasks) return state

    const { nextCursor } = state.bulkTasks.data

    return update(state, {
      bulkTasks: {
        $set: {
          isFetching: false,
          data: {
            tasks, // replace all current records, both fetching and polling fetches max 10 records
            nextCursor, // no nextCursor usage in MVP, but polling does not update the nextCursor
          },
        },
      },
      ongoingBulkTasks: {
        $set: tasks.filter(task => isOngoingTask(task)),
      },
    })
  },
  [getActionSuccess(actionTypes.COMPLETE_BULK_TASK)]: (
    state: ReducerType,
    action: AxiosAction<void, ReturnType<typeof actions.completeBulkTask>>,
  ) => {
    const { taskID } = action.meta.previousAction?.payload?.request?.data || {}
    if (!taskID) return state

    // mock update ongoing task after pushing the file, in case the task fails too fast before start polling bulkTask list -> still able to show the notification
    return update(state, {
      ongoingBulkTasks: {
        $set: [
          {
            taskID,
            status: BulkUploadTaskStatus.TaskNew,
          } as BulkUploadTask,
        ],
      },
    })
  },
})
